/** @jsxImportSource @emotion/react */
import {
  Dispatch,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useState
} from "react";

import { useTranslation } from "react-i18next";

import { v7 as uuidv7 } from "uuid";
import {
  BooleanCompanySetting,
  COMPANY_SETTINGS
} from "@/components/DetailsSection/types";
import { useCompaniesTableStore } from "@/stores/useTableStore";
import { css } from "@emotion/react";
import Tab from "@mui/material/Tab";

import {
  AccountTreeOutlineIcon,
  CorporateFareIcon
} from "@dotnet/telematics-design-system";

import { useAuth } from "../../../../../../context/Auth/AuthProvider";
import { COMPANY_LEVELS } from "../../../../../../Pages/Companies/types";
import { USER_PERMISSIONS } from "../../../../../../roles/types/enums";
import {
  INPUT_LENGTH,
  inputFieldMarginBottom,
  useMediaQueries
} from "../../../../../../shared";
import useCompaniesFormInputs from "../../../../../../shared/hooks/useCompaniesFormInputs/useCompaniesFormInputs";
import Autocomplete from "../../../../../Autocomplete/Autocomplete";
import InputField from "../../../../../InputField/InputField";
import SegmentedToggle from "../../../../../SegmentedToggle/SegmentedToggle";
import { SEGMENTED_TOGGLE_TAB_SIZES } from "../../../../../SegmentedToggle/types";
import AutocompleteDisabledOptionTooltip from "../../../../../Tooltip/AutocompleteDisabledOptionTooltip";
import { PAYLOAD_ID_KEYS } from "../types";
import CreateNewHierarchyContent from "./CreateNewHierarchyDialogContent";
import { useNewHierarchyFormInputs } from "./hooks/useNewHierarchyFormInputs";

export const indexOfLastCompanyLevel = COMPANY_LEVELS.MaxCompaniesLevels - 1;

interface ICreateCompanyDialogContentProps {
  isAllowedToCreateNewHierarchy: boolean;
  companyTabValue: number;
  setCompanyTabValue: Dispatch<SetStateAction<number>>;
  setPayload: Dispatch<any>;
  setIsReady: Dispatch<SetStateAction<boolean>>;
  resetPayloadValues: () => void;
  isLoading: boolean;
}

type SettingsState = Record<BooleanCompanySetting, boolean>;

const CreateCompanyDialogContent = ({
  isAllowedToCreateNewHierarchy,
  companyTabValue,
  setCompanyTabValue,
  setPayload,
  setIsReady,
  resetPayloadValues,
  isLoading
}: ICreateCompanyDialogContentProps) => {
  const { getAccessStatus } = useAuth();
  const { t } = useTranslation();
  const { fromSm, fromMd } = useMediaQueries();

  const { data: tableRowData } = useCompaniesTableStore();

  const isOnlyParentCompanyInAList = tableRowData?.length === 1;

  const checkIfOnlyParentCompanyInAList = (
    valueIfTrue: any,
    valueIfFalse: any
  ) => (isOnlyParentCompanyInAList ? valueIfTrue : valueIfFalse);

  const initialLogisticJournalData = {
    lzCode: "",
    lzCompanyName: ""
  };

  const [logisticJournalData, setLogisticJournalData] = useState(
    initialLogisticJournalData
  );

  const { lzCode, lzCompanyName } = logisticJournalData;

  const [parentCompanyId, setParentCompanyId] = useState<string | null>(
    checkIfOnlyParentCompanyInAList(tableRowData[0].id as string, null)
  );

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [nameInputError, setNameInputError] = useState<string | null>(null);
  const [descriptionInputError, setDescriptionInputError] = useState<
    string | null
  >(null);

  const [settings, setSettings] = useState<SettingsState>({
    [COMPANY_SETTINGS.AutomaticDriverRegistrationEnabled]: true,
    [COMPANY_SETTINGS.AutoInitialVehicleDownload]: true,
    [COMPANY_SETTINGS.AutoDriverFileDownload]: true,
    [COMPANY_SETTINGS.AutoAssignGlobalScheduleToDriver]: true,
    [COMPANY_SETTINGS.AutoAssignVehicleRegistrationNumber]: false,
    [COMPANY_SETTINGS.AutoAssignGlobalScheduleToVehicle]: true
  });

  const onToggleSetting = (setting: BooleanCompanySetting) => () =>
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));

  const {
    validateNameLength,
    handleNameChangeValue,
    validateDescriptionLength,
    handleChangeDescription,
    generateCompanySettings,
    resetAllValues
  } = useCompaniesFormInputs({
    setNameInputError,
    setName,
    setDescription,
    setDescriptionInputError
  });

  const {
    verifyButtonLoading,
    logisticJournalError,
    handleVerify,
    handleLzCodeInput,
    getLzCodeFieldIcon
  } = useNewHierarchyFormInputs(
    logisticJournalData,
    setLogisticJournalData,
    setName
  );

  const hierarchyCreateSelected = companyTabValue === 1;
  const isAllowedToCreateChildCompany = getAccessStatus(
    USER_PERMISSIONS.ManageCompanies
  );
  const enableChildCreateTab = !isLoading && isAllowedToCreateChildCompany;

  const companyName = name?.trim();
  const nameIsValid =
    name &&
    companyName.length <= INPUT_LENGTH.CompanyName &&
    companyName.length >= INPUT_LENGTH.MinCompanyName;

  const descriptionIsValid =
    (description && description.length <= INPUT_LENGTH.Description) ||
    !description;

  const lzCodeOrParentCompanyIdValid = hierarchyCreateSelected
    ? lzCode
    : parentCompanyId;

  const isPayloadReadyToConfirmChild =
    Boolean(lzCodeOrParentCompanyIdValid) &&
    Boolean(name) &&
    Boolean(nameIsValid) &&
    Boolean(descriptionIsValid);

  useEffect(() => {
    const idKey = hierarchyCreateSelected
      ? PAYLOAD_ID_KEYS.LogisticsJournalId
      : PAYLOAD_ID_KEYS.ParentCompanyId;

    setPayload({
      [idKey]: lzCodeOrParentCompanyIdValid,
      name: name.trim(),
      comment: description.trim(),

      [COMPANY_SETTINGS.AutoAssignVehicleRegistrationNumber]:
        settings[COMPANY_SETTINGS.AutoAssignVehicleRegistrationNumber],

      [COMPANY_SETTINGS.AutoInitialVehicleDownload]:
        settings[COMPANY_SETTINGS.AutoInitialVehicleDownload],

      [COMPANY_SETTINGS.AutoAssignGlobalScheduleToVehicle]:
        settings[COMPANY_SETTINGS.AutoAssignGlobalScheduleToVehicle],

      [COMPANY_SETTINGS.AutomaticDriverRegistrationEnabled]:
        settings[COMPANY_SETTINGS.AutomaticDriverRegistrationEnabled],

      [COMPANY_SETTINGS.AutoDriverFileDownload]:
        settings[COMPANY_SETTINGS.AutoDriverFileDownload],

      [COMPANY_SETTINGS.AutoAssignGlobalScheduleToDriver]:
        settings[COMPANY_SETTINGS.AutoAssignGlobalScheduleToDriver]
    });

    setIsReady(isPayloadReadyToConfirmChild);
  }, [
    hierarchyCreateSelected,
    isPayloadReadyToConfirmChild,
    parentCompanyId,
    lzCode,
    name,
    description,
    setIsReady,
    setPayload,
    lzCodeOrParentCompanyIdValid,
    settings
  ]);

  const handleParentCompanyChange = (event: SyntheticEvent, value: any) => {
    event?.preventDefault();
    setParentCompanyId(value.id);
  };

  const getDisabledStatus = () => {
    if (!isAllowedToCreateNewHierarchy || !hierarchyCreateSelected) {
      return false;
    }
    return !lzCompanyName;
  };

  const companySettings = generateCompanySettings({
    assignVehicleRegistrationNumber:
      settings[COMPANY_SETTINGS.AutoAssignVehicleRegistrationNumber],
    handleAutoAssignVehicleRegistrationNumber: onToggleSetting(
      COMPANY_SETTINGS.AutoAssignVehicleRegistrationNumber
    ),

    autoInitialVehicleDownload:
      settings[COMPANY_SETTINGS.AutoInitialVehicleDownload],
    handleToggleAutoInitialVehicleDownload: onToggleSetting(
      COMPANY_SETTINGS.AutoInitialVehicleDownload
    ),

    assignGlobalScheduleToVehicle:
      settings[COMPANY_SETTINGS.AutoAssignGlobalScheduleToVehicle],
    handleToggleAssignGlobalScheduleToVehicle: onToggleSetting(
      COMPANY_SETTINGS.AutoAssignGlobalScheduleToVehicle
    ),

    addNewDriver: settings[COMPANY_SETTINGS.AutomaticDriverRegistrationEnabled],
    handleToggleAddNewDriver: onToggleSetting(
      COMPANY_SETTINGS.AutomaticDriverRegistrationEnabled
    ),

    autoDriverFileDownload: settings[COMPANY_SETTINGS.AutoDriverFileDownload],
    handleToggleAutoDriverFileDownload: onToggleSetting(
      COMPANY_SETTINGS.AutoDriverFileDownload
    ),

    assignGlobalScheduleToDriver:
      settings[COMPANY_SETTINGS.AutoAssignGlobalScheduleToDriver],
    handleToggleAssignGlobalScheduleToDriver: onToggleSetting(
      COMPANY_SETTINGS.AutoAssignGlobalScheduleToDriver
    ),

    isDisabled: getDisabledStatus(),
    readOnly: isLoading
  });

  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    setCompanyTabValue(newValue);
    handleFormReset();
  };

  const handleFormReset = () => {
    resetPayloadValues();
    resetAllValues();
    setLogisticJournalData(initialLogisticJournalData);
  };

  return (
    <>
      {isAllowedToCreateNewHierarchy && (
        <SegmentedToggle
          data-testid="new-company-tabs"
          value={companyTabValue}
          size={SEGMENTED_TOGGLE_TAB_SIZES.Small}
          fullWidth={fromSm}
          tabWidth={fromMd ? "277px" : "100%"}
          onChange={handleTabChange}
          css={css(inputFieldMarginBottom)}
        >
          <Tab
            data-testid="child-company-form-tab"
            label={t("Button##child company")}
            icon={<CorporateFareIcon />}
            className={enableChildCreateTab ? "enabled" : undefined}
            disabled={!enableChildCreateTab}
          />

          <Tab
            data-testid="new-client-form-tab"
            label={t("Button##new client")}
            icon={<AccountTreeOutlineIcon />}
            className={isLoading ? undefined : "enabled"}
            disabled={isLoading}
          />
        </SegmentedToggle>
      )}

      {hierarchyCreateSelected ? (
        <CreateNewHierarchyContent
          verifyButtonLoading={verifyButtonLoading}
          logisticJournalError={logisticJournalError}
          logisticJournalData={logisticJournalData}
          getLzCodeFieldIcon={getLzCodeFieldIcon}
          handleLzCodeInput={handleLzCodeInput}
          handleFormReset={handleFormReset}
          handleVerify={handleVerify}
          isLoading={isLoading}
        />
      ) : (
        <Autocomplete
          data-testid="parent-company-dropdown"
          isRequired
          size="medium"
          labelLeft={t("Dialog##parent company")}
          fullWidth
          disabled={isLoading}
          disableClearable
          value={tableRowData.find(row => row.id === parentCompanyId) ?? null}
          textFieldParams={{
            placeholder: checkIfOnlyParentCompanyInAList(
              undefined,
              t("Dialog##select")
            )
          }}
          getOptionDisabled={option => option.level >= indexOfLastCompanyLevel}
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          options={tableRowData}
          renderOption={(props, option) => (
            <AutocompleteDisabledOptionTooltip
              key={option.id}
              props={props}
              option={option.name}
              tooltipTitle={t(`Tooltip##parent company`, {
                companyName: option.name,
                maxAllowedLevels: COMPANY_LEVELS.MaxCompaniesLevels
              })}
            />
          )}
          onChange={(_, newInputValue) => {
            handleParentCompanyChange(_, newInputValue);
          }}
          customStyle={inputFieldMarginBottom}
        />
      )}

      <InputField
        key={`${companyTabValue}-company-name-${getDisabledStatus()}`}
        id={uuidv7()}
        testId="company-name-input-field"
        size="medium"
        fullWidth
        required
        disabled={getDisabledStatus() || isLoading}
        value={name}
        errorText={nameIsValid ? "" : nameInputError}
        onBlur={validateNameLength}
        onChange={handleNameChangeValue}
        labelLeft={
          hierarchyCreateSelected
            ? t("Dialog##company name in tachoSync")
            : t("Dialog##company name")
        }
        placeholder={
          getDisabledStatus()
            ? undefined
            : t("Dialog##company name placeholder")
        }
        customStyle={inputFieldMarginBottom}
      />

      <InputField
        key={`${companyTabValue}-description-${getDisabledStatus()}`}
        id={uuidv7()}
        data-testid="company-description-input-field"
        size="medium"
        fullWidth
        disabled={getDisabledStatus() || isLoading}
        value={description}
        errorText={descriptionIsValid ? "" : descriptionInputError}
        onBlur={validateDescriptionLength}
        onChange={handleChangeDescription}
        labelLeft={t("Dialog##description")}
        placeholder={getDisabledStatus() ? undefined : t("Dialog##enter here")}
        customStyle={inputFieldMarginBottom}
      />

      {companySettings}
    </>
  );
};

export default CreateCompanyDialogContent;
